import EmptyPageContent from '@app/ccf/components/empty-page-content';
import Page from '@app/components/layout/page/page';
import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import { useGetPurchasedPackagingCoverage } from '@shared/api/hooks/purchased-goods/packaging';
import { useGetValidReportingPeriods } from '@shared/api/hooks/reports/periods';
import Text from '@shared/components/content/text';
import DataCoverageCard from '@shared/components/data-coverage/data-coverage-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { generateMonthsForYear } from '@shared/utils/date-formatters';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PurchasedPackagingDataCoveragePage() {
  const navigate = useNavigate();
  const { data: reportingYears } = useGetValidReportingPeriods();
  const { data: coverage, status } = useGetPurchasedPackagingCoverage();
  const [year, setYear] = useState<string | undefined>();
  const months = useMemo(
    () => (reportingYears ? generateMonthsForYear(reportingYears[0]) : []),
    [reportingYears]
  );

  useEffect(() => {
    if (reportingYears && reportingYears.length > 0) {
      setYear(reportingYears[0].name);
    }
  }, [reportingYears]);

  const yearHasNoData = useMemo(() => {
    if (!year || !coverage) return false;
    const yearData = coverage.sites.some((site) =>
      site.years.some((y) => {
        if (y.name !== year) return false;
        return y.hasData || y.months.some((m) => m.hasData);
      })
    );
    return !yearData;
  }, [year, coverage]);

  return (
    <Page
      name="Purchased Packaging - Data Coverage"
      page="subpage"
      status={status}
    >
      <div className="max-w-4xl">
        <div className="mb-8 flex items-center justify-between space-x-4">
          <Text variant="subtle" className="max-w-lg">
            For each reporting year, check whether your imported data covers the
            full year for each of your sites.
          </Text>
          <Select defaultValue={year} value={year} onValueChange={setYear}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              {reportingYears?.map(({ name }) => (
                <SelectItem key={name} value={name}>
                  <Text>{name}</Text>
                </SelectItem>
              )) || []}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-4">
          {!yearHasNoData &&
            year &&
            coverage &&
            coverage.sites.map((site) => {
              const yearData = site.years.find((y) => y.name === year);
              if (!yearData) return null;
              return (
                <DataCoverageCard
                  key={site.name}
                  data={site}
                  yearName={year}
                  monthLabels={months}
                />
              );
            })}
          {yearHasNoData && (
            <EmptyPageContent
              description={`No purchase data added for ${year}`}
              primaryActionText="Add packaging data"
              primaryAction={() =>
                navigate(purchasedGoodsRoutes.PURCHASED_GOODS_IMPORT)
              }
            />
          )}
        </div>
      </div>
    </Page>
  );
}
